import { useEffect, useRef } from 'react';

/**
 * Warn user before leaving the current page when there are unsaved changes.
 *
 * Covers:
 * - Browser reload / close tab / navigate away (via `beforeunload`)
 * - In-app SPA navigation (by capturing link clicks + back/forward `popstate`)
 *
 * Notes:
 * - Modern browsers ignore custom text for `beforeunload` dialogs; they show a generic message.
 * - This hook uses capturing listeners to intercept navigation attempts early.
 * - Back button interception only activates when there are unsaved changes.
 *
 * @param {object} options
 * @param {boolean} options.enabled - Whether to enable the warning behavior.
 * @param {boolean|function} options.hasUnsavedChanges - Boolean or function returning boolean.
 * @param {string|function} [options.message] - Confirm dialog message for in-app navigation.
 * @param {function} [options.onDecision] - Callback invoked with decision info.
 */
interface DecisionMeta {
  reason?: string;
  toUrl?: string;
}

interface UseUnsavedChangesWarningOptions {
  enabled?: boolean;
  hasUnsavedChanges?: boolean | (() => boolean);
  message?: string | (() => string);
  onDecision?: (info: { ok: boolean } & DecisionMeta) => void;
}

export default function useUnsavedChangesWarning({
  enabled = true,
  hasUnsavedChanges = false,
  message = 'You have unsaved changes. Are you sure you want to leave this page?',
  onDecision,
}: UseUnsavedChangesWarningOptions = {}) {
  // Track whether we have a guard state pushed
  const guardPushedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    // Helper to evaluate hasUnsavedChanges (can be boolean or function)
    const getHasUnsaved = () => {
      try {
        return typeof hasUnsavedChanges === 'function'
          ? !!hasUnsavedChanges()
          : !!hasUnsavedChanges;
      } catch (_e) {
        return true;
      }
    };

    const getMessage = () => {
      try {
        return typeof message === 'function' ? `${message()}` : `${message}`;
      } catch (_e) {
        return 'You have unsaved changes. Are you sure you want to leave this page?';
      }
    };

    const notifyDecision = (ok: boolean, meta: DecisionMeta) => {
      if (typeof onDecision === 'function') {
        try {
          onDecision({ ok, ...meta });
        } catch (_e) {
          // ignore
        }
      }
    };

    // Push guard state if there are unsaved changes and guard not yet pushed
    const ensureGuard = () => {
      if (getHasUnsaved() && !guardPushedRef.current) {
        try {
          window.history.pushState(
            { __unsaved_changes_guard__: true },
            '',
            window.location.href
          );
          guardPushedRef.current = true;
        } catch (_e) {
          // ignore
        }
      }
    };

    // Initial guard push
    ensureGuard();

    // 1) Reload / close / external navigation
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!getHasUnsaved()) {
        return undefined;
      }
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    // 2) Back/forward navigation
    const handlePopState = (_e: PopStateEvent) => {
      // If we didn't push a guard, this is normal navigation - let it through
      if (!guardPushedRef.current) {
        return;
      }

      // Guard was popped
      guardPushedRef.current = false;

      // Check if there are still unsaved changes
      if (!getHasUnsaved()) {
        // No unsaved changes - complete the navigation
        setTimeout(() => window.history.back(), 0);
        return;
      }

      // Show confirmation dialog
      const ok = window.confirm(getMessage());
      notifyDecision(ok, { reason: 'popstate' });

      if (ok) {
        // User accepted - complete the navigation
        setTimeout(() => window.history.back(), 0);
      } else {
        // User cancelled - re-push guard
        try {
          window.history.pushState(
            { __unsaved_changes_guard__: true },
            '',
            window.location.href
          );
          guardPushedRef.current = true;
        } catch (_err) {
          // ignore
        }
      }
    };

    // 3) Link clicks
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
        return;
      }

      // Find anchor element
      let el = event.target as HTMLElement | null;
      while (el && el.tagName !== 'A') {
        el = el.parentElement;
      }
      if (!el) return;

      const href = el.getAttribute('href');
      if (!href || href.startsWith('#')) return;

      const target = el.getAttribute('target');
      if (target && target.toLowerCase() !== '_self') return;

      if (el.hasAttribute('download')) return;

      // Check if navigating to different URL
      try {
        const toUrl = new URL(href, window.location.href).toString();
        if (toUrl === window.location.href) return;
      } catch (_e) {
        // ignore URL parse errors
      }

      // Check for unsaved changes
      if (!getHasUnsaved()) return;

      const ok = window.confirm(getMessage());
      notifyDecision(ok, { reason: 'link', toUrl: href });

      if (!ok) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('click', handleClick, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleClick, true);
    };
  }, [enabled, hasUnsavedChanges, message, onDecision]);
}
